import { apiClient } from "@/lib/apiClient";
import {
  CrudService,
  FetchOptions,
  SearchOptions,
  CreateRequest,
  BulkUpdateRequest,
  BulkDeleteRequest,
} from "@/types/crud";
import {
  MarketingCampaign,
  MarketingCampaignFilters,
  MarketingAutomation,
  MarketingAutomationFilters,
  CreateAbandonedCartBulkMessageDto,
  UpdateAbandonedCartBulkMessageDto,
  AbandonedCartFilters,
  MarketingTemplate,
  MarketingTemplateFilters,
} from "@/types/models/marketing";

// Marketing Campaign Service
class MarketingCampaignServiceImpl
  implements CrudService<MarketingCampaign, MarketingCampaignFilters>
{
  async getAll(options: FetchOptions<MarketingCampaignFilters> = {}) {
    const params: Record<string, string> = {};

    if (options.page) params.page = options.page.toString();
    if (options.pageSize) params.pageSize = options.pageSize.toString();
    if (options.sortBy) params.sortBy = options.sortBy;
    if (options.sortOrder) params.sortOrder = options.sortOrder;

    if (options.filters) {
      if (options.filters.search) params.search = options.filters.search;
      if (options.filters.channel) params.channel = options.filters.channel;
      if (options.filters.status) params.status = options.filters.status;
      if (options.filters.recipient_type)
        params.recipient_type = options.filters.recipient_type;
    }

    return await apiClient.getPaginated(
      "/api/v1/admin/:storeId/marketing/campaigns",
      params,
    );
  }

  async getById(campaignId: string) {
    return await apiClient.get(
      `/api/v1/admin/:storeId/marketing/campaigns/${campaignId}`,
    );
  }

  async create(campaign: CreateRequest<MarketingCampaign>) {
    return await apiClient.post(
      "/api/v1/admin/:storeId/marketing/campaigns",
      campaign,
    );
  }

  async update(campaignId: string, campaign: Partial<MarketingCampaign>) {
    return await apiClient.put(
      `/api/v1/admin/:storeId/marketing/campaigns/${campaignId}`,
      campaign,
    );
  }

  async delete(campaignId: string) {
    return await apiClient.delete(
      `/api/v1/admin/:storeId/marketing/campaigns/${campaignId}`,
    );
  }

  async search(options: SearchOptions<MarketingCampaignFilters>) {
    const params: Record<string, string> = {
      q: options.query,
    };

    if (options.page) params.page = options.page.toString();
    if (options.pageSize) params.pageSize = options.pageSize.toString();

    if (options.filters) {
      if (options.filters.channel) params.channel = options.filters.channel;
      if (options.filters.status) params.status = options.filters.status;
      if (options.filters.recipient_type)
        params.recipient_type = options.filters.recipient_type;
    }

    return await apiClient.getPaginated(
      "/api/v1/admin/:storeId/marketing/campaigns/search",
      params,
    );
  }

  async bulkUpdate(data: BulkUpdateRequest) {
    return await apiClient.put(
      "/api/v1/admin/:storeId/marketing/campaigns/bulk",
      data,
    );
  }

  async bulkDelete(data: BulkDeleteRequest) {
    return await apiClient.delete(
      "/api/v1/admin/:storeId/marketing/campaigns/bulk",
      { ids: data.ids },
    );
  }

  async sendCampaign(campaignId: string) {
    return await apiClient.post(
      `/api/v1/admin/:storeId/marketing/campaigns/${campaignId}/send`,
    );
  }
}

// Marketing Automation Service
class MarketingAutomationServiceImpl
  implements CrudService<MarketingAutomation, MarketingAutomationFilters>
{
  async getAll(options: FetchOptions<MarketingAutomationFilters> = {}) {
    const params: Record<string, string> = {};

    if (options.page) params.page = options.page.toString();
    if (options.pageSize) params.pageSize = options.pageSize.toString();
    if (options.sortBy) params.sortBy = options.sortBy;
    if (options.sortOrder) params.sortOrder = options.sortOrder;

    if (options.filters) {
      if (options.filters.search) params.search = options.filters.search;
      if (options.filters.channel) params.channel = options.filters.channel;
      if (options.filters.trigger_type)
        params.trigger_type = options.filters.trigger_type;
      if (options.filters.enabled !== undefined)
        params.enabled = options.filters.enabled.toString();
    }

    return await apiClient.getPaginated(
      "/api/v1/admin/:storeId/marketing/automation",
      params,
    );
  }

  async getById(automationId: string) {
    return await apiClient.get(
      `/api/v1/admin/:storeId/marketing/automation/${automationId}`,
    );
  }

  async create(automation: CreateRequest<MarketingAutomation>) {
    return await apiClient.post(
      "/api/v1/admin/:storeId/marketing/automation",
      automation,
    );
  }

  async update(automationId: string, automation: Partial<MarketingAutomation>) {
    return await apiClient.put(
      `/api/v1/admin/:storeId/marketing/automation/${automationId}`,
      automation,
    );
  }

  async delete(automationId: string) {
    return await apiClient.delete(
      `/api/v1/admin/:storeId/marketing/automation/${automationId}`,
    );
  }

  async search(options: SearchOptions<MarketingAutomationFilters>) {
    const params: Record<string, string> = {
      q: options.query,
    };

    if (options.page) params.page = options.page.toString();
    if (options.pageSize) params.pageSize = options.pageSize.toString();

    if (options.filters) {
      if (options.filters.channel) params.channel = options.filters.channel;
      if (options.filters.trigger_type)
        params.trigger_type = options.filters.trigger_type;
      if (options.filters.enabled !== undefined)
        params.enabled = options.filters.enabled.toString();
    }

    return await apiClient.getPaginated(
      "/api/v1/admin/:storeId/marketing/automation/search",
      params,
    );
  }

  async bulkUpdate(data: BulkUpdateRequest) {
    return await apiClient.put(
      "/api/v1/admin/:storeId/marketing/automation/bulk",
      data,
    );
  }

  async bulkDelete(data: BulkDeleteRequest) {
    return await apiClient.delete(
      "/api/v1/admin/:storeId/marketing/automation/bulk",
      { ids: data.ids },
    );
  }
}

// Abandoned Cart Service
class AbandonedCartServiceImpl {
  async getBulkMessages(options: FetchOptions<AbandonedCartFilters> = {}) {
    const params: Record<string, string> = {};

    if (options.page) params.page = options.page.toString();
    if (options.pageSize) params.pageSize = options.pageSize.toString();
    if (options.sortBy) params.sortBy = options.sortBy;
    if (options.sortOrder) params.sortOrder = options.sortOrder;

    if (options.filters) {
      if (options.filters.search) params.search = options.filters.search;
      if (options.filters.channel) params.channel = options.filters.channel;
      if (options.filters.delivery_status)
        params.delivery_status = options.filters.delivery_status;
    }

    return await apiClient.getPaginated(
      "/api/v1/admin/:storeId/marketing/abandoned-cart/bulk-messages",
      params,
    );
  }

  async createBulkMessage(message: CreateAbandonedCartBulkMessageDto) {
    return await apiClient.post(
      "/api/v1/admin/:storeId/marketing/abandoned-cart/bulk-messages",
      message,
    );
  }

  async updateBulkMessage(
    messageId: string,
    message: UpdateAbandonedCartBulkMessageDto,
  ) {
    return await apiClient.put(
      `/api/v1/admin/:storeId/marketing/abandoned-cart/bulk-messages/${messageId}`,
      message,
    );
  }

  async deleteBulkMessage(messageId: string) {
    return await apiClient.delete(
      `/api/v1/admin/:storeId/marketing/abandoned-cart/bulk-messages/${messageId}`,
    );
  }

  async sendBulkMessage(messageId: string) {
    return await apiClient.post(
      `/api/v1/admin/:storeId/marketing/abandoned-cart/bulk-messages/${messageId}/send`,
    );
  }

  async getCustomers(options: FetchOptions<AbandonedCartFilters> = {}) {
    const params: Record<string, string> = {};

    if (options.page) params.page = options.page.toString();
    if (options.pageSize) params.pageSize = options.pageSize.toString();
    if (options.sortBy) params.sortBy = options.sortBy;
    if (options.sortOrder) params.sortOrder = options.sortOrder;

    return await apiClient.getPaginated(
      "/api/v1/admin/:storeId/marketing/abandoned-cart/customers",
      params,
    );
  }
}

// Marketing Template Service
class MarketingTemplateServiceImpl
  implements CrudService<MarketingTemplate, MarketingTemplateFilters>
{
  async getAll(options: FetchOptions<MarketingTemplateFilters> = {}) {
    const params: Record<string, string> = {};

    if (options.page) params.page = options.page.toString();
    if (options.pageSize) params.pageSize = options.pageSize.toString();
    if (options.sortBy) params.sortBy = options.sortBy;
    if (options.sortOrder) params.sortOrder = options.sortOrder;

    if (options.filters) {
      if (options.filters.search) params.search = options.filters.search;
      if (options.filters.channel) params.channel = options.filters.channel;
      if (options.filters.template_type)
        params.template_type = options.filters.template_type;
    }

    return await apiClient.getPaginated(
      "/api/v1/admin/:storeId/marketing/templates",
      params,
    );
  }

  async getById(templateId: string) {
    return await apiClient.get(
      `/api/v1/admin/:storeId/marketing/templates/${templateId}`,
    );
  }

  async create(template: CreateRequest<MarketingTemplate>) {
    return await apiClient.post(
      "/api/v1/admin/:storeId/marketing/templates",
      template,
    );
  }

  async update(templateId: string, template: Partial<MarketingTemplate>) {
    return await apiClient.put(
      `/api/v1/admin/:storeId/marketing/templates/${templateId}`,
      template,
    );
  }

  async delete(templateId: string) {
    return await apiClient.delete(
      `/api/v1/admin/:storeId/marketing/templates/${templateId}`,
    );
  }

  async search(options: SearchOptions<MarketingTemplateFilters>) {
    const params: Record<string, string> = {
      q: options.query,
    };

    if (options.page) params.page = options.page.toString();
    if (options.pageSize) params.pageSize = options.pageSize.toString();

    if (options.filters) {
      if (options.filters.channel) params.channel = options.filters.channel;
      if (options.filters.template_type)
        params.template_type = options.filters.template_type;
    }

    return await apiClient.getPaginated(
      "/api/v1/admin/:storeId/marketing/templates/search",
      params,
    );
  }

  async bulkUpdate(data: BulkUpdateRequest) {
    return await apiClient.put(
      "/api/v1/admin/:storeId/marketing/templates/bulk",
      data,
    );
  }

  async bulkDelete(data: BulkDeleteRequest) {
    return await apiClient.delete(
      "/api/v1/admin/:storeId/marketing/templates/bulk",
      { ids: data.ids },
    );
  }
}

// Export service instances
export const marketingCampaignService = new MarketingCampaignServiceImpl();
export const marketingAutomationService = new MarketingAutomationServiceImpl();
export const abandonedCartService = new AbandonedCartServiceImpl();
export const marketingTemplateService = new MarketingTemplateServiceImpl();

// Legacy exports for backwards compatibility
export const getMarketingCampaigns = marketingCampaignService.getAll.bind(
  marketingCampaignService,
);
export const getMarketingCampaignById = marketingCampaignService.getById.bind(
  marketingCampaignService,
);
export const createMarketingCampaign = marketingCampaignService.create.bind(
  marketingCampaignService,
);
export const updateMarketingCampaign = marketingCampaignService.update.bind(
  marketingCampaignService,
);
export const deleteMarketingCampaign = marketingCampaignService.delete.bind(
  marketingCampaignService,
);
export const searchMarketingCampaigns = marketingCampaignService.search.bind(
  marketingCampaignService,
);

export const getMarketingAutomations = marketingAutomationService.getAll.bind(
  marketingAutomationService,
);
export const getMarketingAutomationById =
  marketingAutomationService.getById.bind(marketingAutomationService);
export const createMarketingAutomation = marketingAutomationService.create.bind(
  marketingAutomationService,
);
export const updateMarketingAutomation = marketingAutomationService.update.bind(
  marketingAutomationService,
);
export const deleteMarketingAutomation = marketingAutomationService.delete.bind(
  marketingAutomationService,
);
export const searchMarketingAutomations =
  marketingAutomationService.search.bind(marketingAutomationService);

export const getAbandonedCartBulkMessages =
  abandonedCartService.getBulkMessages.bind(abandonedCartService);
export const createAbandonedCartBulkMessage =
  abandonedCartService.createBulkMessage.bind(abandonedCartService);
export const updateAbandonedCartBulkMessage =
  abandonedCartService.updateBulkMessage.bind(abandonedCartService);
export const deleteAbandonedCartBulkMessage =
  abandonedCartService.deleteBulkMessage.bind(abandonedCartService);
export const sendAbandonedCartBulkMessage =
  abandonedCartService.sendBulkMessage.bind(abandonedCartService);
export const getAbandonedCartCustomers =
  abandonedCartService.getCustomers.bind(abandonedCartService);

export const getMarketingTemplates = marketingTemplateService.getAll.bind(
  marketingTemplateService,
);
export const getMarketingTemplateById = marketingTemplateService.getById.bind(
  marketingTemplateService,
);
export const createMarketingTemplate = marketingTemplateService.create.bind(
  marketingTemplateService,
);
export const updateMarketingTemplate = marketingTemplateService.update.bind(
  marketingTemplateService,
);
export const deleteMarketingTemplate = marketingTemplateService.delete.bind(
  marketingTemplateService,
);
export const searchMarketingTemplates = marketingTemplateService.search.bind(
  marketingTemplateService,
);
